import { useState } from "react";
import { toast } from "sonner";
import { Send, RotateCcw, Archive as ArchiveIcon, Copy, GitFork, Heart, Link2, Paperclip, FilePlus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  useSubmitLibraryVersionForReview, useReturnLibraryVersionToDraft, usePublishLibraryVersion,
  useRetireLibraryVersion, useCreateNextDraftVersion, useDuplicateLibraryItem, useForkLibraryItemToWorkspace,
  useToggleLibraryFavorite, useCoachOwnWorkspaces, useLibraryLinks, useCreateLibraryLink, useDeleteLibraryLink,
  useLibraryAssets, useAddLibraryAsset, useRemoveLibraryAsset,
} from "@/lib/coachWorkbenchQueries";

const ASSET_ROLES = ["worksheet", "instructions", "coach_guide", "demo_video", "demo_audio", "slides", "transcript", "reference", "thumbnail", "preview"];

/**
 * Master v5 Prompt 20 — every lifecycle action for one LibraryItem/
 * latest version: submit-for-review/return-to-draft/publish/retire/
 * edit-as-new-version, duplicate, Workspace-variant fork, favorite,
 * framework<->exercise links and role-tagged assets. Nothing here
 * publishes automatically — every transition is an explicit click.
 */
export function LibraryItemManagePanel({ item, latest, isFavorite }) {
  const submitReview = useSubmitLibraryVersionForReview();
  const returnDraft = useReturnLibraryVersionToDraft();
  const publish = usePublishLibraryVersion();
  const retire = useRetireLibraryVersion();
  const nextDraft = useCreateNextDraftVersion();
  const duplicate = useDuplicateLibraryItem();
  const fork = useForkLibraryItemToWorkspace();
  const toggleFavorite = useToggleLibraryFavorite();
  const { data: workspacesData } = useCoachOwnWorkspaces();
  const workspaces = workspacesData?.items || [];
  const [forkTarget, setForkTarget] = useState("");

  const { data: linksData } = useLibraryLinks(item.lib_id);
  const createLink = useCreateLibraryLink();
  const deleteLink = useDeleteLibraryLink();
  const [linkTargetId, setLinkTargetId] = useState("");

  const { data: assetsData } = useLibraryAssets(item.lib_id);
  const addAsset = useAddLibraryAsset();
  const removeAsset = useRemoveLibraryAsset();
  const [assetForm, setAssetForm] = useState({ workspace_id: "", fas_id: "", role: "worksheet" });

  async function run(mutation, args, okMsg) {
    try {
      const res = await mutation.mutateAsync(args);
      if (res?.duplicate_warning?.length) {
        toast.warning(`Heads up — this looks identical to ${res.duplicate_warning.map((d) => `${d.human_code} "${d.title}"`).join(", ")}. Published anyway; nothing was merged.`);
      } else if (okMsg) {
        toast.success(okMsg);
      }
    } catch (e) {
      toast.error(e.body?.detail || "That action failed");
    }
  }

  async function handleAddLink() {
    if (!linkTargetId.trim()) return;
    try {
      await createLink.mutateAsync({ libId: item.lib_id, toLibId: linkTargetId.trim() });
      setLinkTargetId("");
      toast.success("Linked");
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't create that link — check the lib_id is catalog-visible to you");
    }
  }

  async function handleAddAsset() {
    if (!assetForm.workspace_id || !assetForm.fas_id.trim()) return toast.error("Workspace and File id are required");
    try {
      await addAsset.mutateAsync({ libId: item.lib_id, libv_id: latest.libv_id, ...assetForm, fas_id: assetForm.fas_id.trim() });
      setAssetForm({ workspace_id: "", fas_id: "", role: "worksheet" });
      toast.success("Asset attached");
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't attach that File");
    }
  }

  const status = latest?.status;

  return (
    <div className="mt-2 pt-2 border-t border-ink/10 space-y-3" data-testid={`library-manage-panel-${item.lib_id}`}>
      {(latest?.use_when || latest?.develops) && (
        <div className="text-[11px] text-ink-soft space-y-0.5">
          {latest.use_when && <p><span className="font-medium">Use when:</span> {latest.use_when}</p>}
          {latest.develops && <p><span className="font-medium">Develops:</span> {latest.develops}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => toggleFavorite.mutate(item.lib_id)} className={`text-xs flex items-center gap-1 ${isFavorite ? "text-terracotta" : "text-ink-soft hover:text-ink"}`} data-testid={`library-favorite-toggle-${item.lib_id}`}>
          <Heart size={12} fill={isFavorite ? "currentColor" : "none"} /> {isFavorite ? "Favorited" : "Favorite"}
        </button>

        {status === "draft" && (
          <>
            <button onClick={() => run(submitReview, { libId: item.lib_id, libvId: latest.libv_id }, "Submitted for review")} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`library-submit-review-${item.lib_id}`}>
              <Send size={11} /> Submit for review
            </button>
            <button onClick={() => run(publish, { libId: item.lib_id, libvId: latest.libv_id }, "Published")} className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid={`library-publish-${item.lib_id}`}>
              <Send size={11} /> Publish
            </button>
          </>
        )}
        {status === "review" && (
          <>
            <button onClick={() => run(returnDraft, { libId: item.lib_id, libvId: latest.libv_id }, "Back to draft")} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`library-return-draft-${item.lib_id}`}>
              <RotateCcw size={11} /> Return to draft
            </button>
            <button onClick={() => run(publish, { libId: item.lib_id, libvId: latest.libv_id }, "Published")} className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid={`library-publish-${item.lib_id}`}>
              <Send size={11} /> Publish
            </button>
          </>
        )}
        {status === "published" && (
          <>
            <button onClick={() => run(nextDraft, item.lib_id, "New draft version created")} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`library-edit-new-version-${item.lib_id}`}>
              <RotateCcw size={11} /> Edit as new version
            </button>
            <button onClick={() => run(retire, { libId: item.lib_id, libvId: latest.libv_id }, "Retired — past assignments keep resolving it exactly as before")} className="text-xs text-ink-soft hover:text-red-600 flex items-center gap-1" data-testid={`library-retire-${item.lib_id}`}>
              <ArchiveIcon size={11} /> Retire
            </button>
          </>
        )}

        <button onClick={() => run(duplicate, item.lib_id, "Duplicated")} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`library-duplicate-${item.lib_id}`}>
          <Copy size={11} /> Duplicate
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <select value={forkTarget} onChange={(e) => setForkTarget(e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid={`library-fork-workspace-select-${item.lib_id}`}>
          <option value="">Fork into Workspace…</option>
          {workspaces.map((w) => <option key={w.wrk_id} value={w.wrk_id}>{w.title}</option>)}
        </select>
        <button
          onClick={() => forkTarget && run(fork, { libId: item.lib_id, workspaceId: forkTarget }, "Workspace variant created")}
          disabled={!forkTarget}
          className="text-xs text-ink-soft hover:text-ink flex items-center gap-1 disabled:opacity-40"
          data-testid={`library-fork-${item.lib_id}`}
        >
          <GitFork size={11} /> Fork
        </button>
      </div>

      <div data-testid={`library-links-${item.lib_id}`}>
        <p className="text-[11px] text-ink-soft flex items-center gap-1 mb-1"><Link2 size={11} /> Linked frameworks/exercises</p>
        <div className="flex flex-wrap gap-1 mb-1">
          {(linksData?.items || []).map((l) => (
            <span key={l.lnk_id} className="text-[11px] bg-ink/5 rounded px-1.5 py-0.5 flex items-center gap-1">
              {l.linked_item ? `${l.linked_item.human_code} ${l.linked_item.title}` : l.linked_lib_id}
              <button onClick={() => deleteLink.mutate({ lnkId: l.lnk_id, libId: item.lib_id })} data-testid={`library-unlink-${l.lnk_id}`}>×</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Input placeholder="Paste a lib_id to link" value={linkTargetId} onChange={(e) => setLinkTargetId(e.target.value)} className="h-6 text-xs" data-testid={`library-link-input-${item.lib_id}`} />
          <button onClick={handleAddLink} className="text-xs text-terracotta" data-testid={`library-link-submit-${item.lib_id}`}>Link</button>
        </div>
      </div>

      <div data-testid={`library-assets-${item.lib_id}`}>
        <p className="text-[11px] text-ink-soft flex items-center gap-1 mb-1"><Paperclip size={11} /> Assets</p>
        <div className="space-y-0.5 mb-1">
          {(assetsData?.items || []).map((a) => (
            <div key={a.lba_id} className="text-[11px] flex items-center gap-1.5">
              <span className="bg-ink/5 rounded px-1.5 py-0.5">{a.role}</span>
              <span className="text-ink-soft">{a.display_filename}</span>
              <button onClick={() => removeAsset.mutate({ libId: item.lib_id, lbaId: a.lba_id })} data-testid={`library-remove-asset-${a.lba_id}`}>×</button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <select value={assetForm.workspace_id} onChange={(e) => setAssetForm((f) => ({ ...f, workspace_id: e.target.value }))} className="border border-ink/15 rounded-md text-xs px-1 py-1 bg-white" data-testid={`library-asset-workspace-${item.lib_id}`}>
            <option value="">Workspace…</option>
            {workspaces.map((w) => <option key={w.wrk_id} value={w.wrk_id}>{w.title}</option>)}
          </select>
          <Input placeholder="fas_id" value={assetForm.fas_id} onChange={(e) => setAssetForm((f) => ({ ...f, fas_id: e.target.value }))} className="h-6 text-xs w-28" data-testid={`library-asset-fasid-${item.lib_id}`} />
          <select value={assetForm.role} onChange={(e) => setAssetForm((f) => ({ ...f, role: e.target.value }))} className="border border-ink/15 rounded-md text-xs px-1 py-1 bg-white" data-testid={`library-asset-role-${item.lib_id}`}>
            {ASSET_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={handleAddAsset} className="text-xs text-terracotta flex items-center gap-1" data-testid={`library-asset-submit-${item.lib_id}`}><FilePlus size={11} /> Attach</button>
        </div>
      </div>
    </div>
  );
}
